import React, { useContext, useEffect, ReactNode, MouseEvent, Dispatch, SetStateAction, useRef } from 'react';
import { Modal } from '../../components/Modal';
import { ConvertStringToReact } from '../../components/ConvertStringToReact';
import { AlertBanner } from '../../components/AlertBanner';
import { CommonButton } from '../../components/CommonButton';
import { Checkbox } from '../../components/Checkbox';
import { Spinner } from '../../components/Spinner';
import { InputMap } from '../../components/InputMap';
import { ContentContext } from '../../context/ContentContext';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import BoxWrapper from '../../components/BoxWrapper';
import styles from './ContactSales.module.css';

import { Formik, Form, Field, FormikProps } from 'formik';
import { InitialValuesType, InputMapRef, SendAsyncPostObject } from '~/common/types';
import { FORM_TYPES } from '~/common/consts';
import { TrustBadge } from '~/common/components/TrustBadge';

interface ContactSalesProps {
  toggleModalState: (state: boolean) => void;
  isModalOpen: boolean;
  formikRef: React.MutableRefObject<FormikProps<SendAsyncPostObject> | null>;
  initialValues: InitialValuesType;
  handleSub: (values: SendAsyncPostObject) => Promise<boolean>;
  unsubscribeClick: (e: React.MouseEvent) => void;
  displayUnsubscribeCheckbox: boolean;
  optOutOfEmails: boolean;
  setOptOutOfEmails: Dispatch<SetStateAction<boolean>>;
  setClosedBanner: Dispatch<SetStateAction<boolean>>;
  closedBanner: boolean;
  setTextareaExistsSetState: () => void;
  formSubmitting: boolean;
}

export const ContactSalesView = (props: ContactSalesProps) => {
  const {
    toggleModalState,
    isModalOpen,
    formikRef,
    initialValues,
    unsubscribeClick,
    displayUnsubscribeCheckbox,
    optOutOfEmails,
    setOptOutOfEmails,
    closedBanner,
    setClosedBanner,
    handleSub,
    setTextareaExistsSetState,
    formSubmitting,
  } = props;

  const data = useContext(ContentContext);
  const newForm: boolean = window.newForm;

  const Wrapper = ({ children }: { children: ReactNode }) => {
    if (newForm) {
      return <>{children}</>;
    } else {
      return <BoxWrapper>{children}</BoxWrapper>;
    }
  };

  interface PropsType {
    isSubmitting: boolean;
    errors: Record<string, any>;
    isValidating: boolean;
    submitCount: number;
    isValid: boolean;
  }

  const Effect: React.FC<PropsType> = (props) => {
    useEffect(() => {
      const errorArr = Object.keys(props.errors);
      if (props.isSubmitting && errorArr.length > 0 && props.isValidating) {
        gtagEnvelope('event', 'form_error', {
          form_url: window.location.pathname,
          error_message: 'empty-fields',
        });
        if (errorArr.length === 1 && props.errors['email'] === false) {
          setClosedBanner(false);
        } else {
          setClosedBanner(true);
        }
      }
    }, [props.submitCount]);

    return null;
  };

  const inputMapRef = useRef<InputMapRef>(null);

  const disableSubmitButton = () => {
    if (window.LPFormData?.formType === FORM_TYPES.PARTNER) {
      const emailBlocked = inputMapRef.current?.isCheckingEmailOrNotBusinessEmail();

      if (emailBlocked) {
        return emailBlocked;
      } else {
        return formSubmitting;
      }
    } else {
      return formSubmitting;
    }
  };

  return (
    <>
      <Modal
        setModalState={toggleModalState}
        modalState={isModalOpen}
        modalType="large"
        bodyCopy="We could not process your request."
        closeButtonCopy="Close"
        headlineCopy="Oops"
        isModalClosable={true}
        transparent={false}
      />

      <h2 className={styles.header}>{data!.title}</h2>
      <AlertBanner closedBanner={closedBanner} setClosedBanner={setClosedBanner} type={'error'}>
        <ConvertStringToReact htmlString={data!.globalError} />
      </AlertBanner>
      <Formik
        validateOnMount={true}
        validateOnChange={true}
        validateOnBlur={true}
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={async (values) => {
          await handleSub(values);
        }}
      >
        {({ errors, touched, values, isSubmitting, isValidating, isValid, submitCount }) => {
          return (
            <Form>
              <Wrapper>
                <Effect
                  submitCount={submitCount}
                  isValidating={isValidating}
                  isValid={isValid}
                  errors={errors}
                  isSubmitting={isSubmitting}
                />
                <InputMap
                  formikTouched={touched}
                  formikErrors={errors}
                  formikValues={values}
                  formikRef={formikRef}
                  setTextareaExistsSetState={setTextareaExistsSetState}
                  setClosedBanner={setClosedBanner}
                  ref={inputMapRef}
                />
                <CommonButton
                  id="FORM_SUBMIT"
                  type="submit"
                  onClick={(event: MouseEvent) => {
                    gtagEnvelope('event', 'cta_submit_click', {
                      cta_name: (event.target as HTMLButtonElement)?.innerHTML || 'Submit',
                      cta_url: `${window.location.origin}${window.location.pathname}/thank-you`,
                      cta_position: 'Lead gen form',
                    });
                  }}
                  disabled={disableSubmitButton()}
                >
                  {formSubmitting ? <Spinner colorBottom="#751a17" colorTop="#fff" /> : data!.sendButton}
                </CommonButton>
                <div className={styles.termsWrap} onClick={unsubscribeClick}>
                  <p className={styles.terms}>
                    <ConvertStringToReact htmlString={data!.terms} />
                  </p>
                  {displayUnsubscribeCheckbox && (
                    <Field
                      name="optOutOfEmails"
                      as={Checkbox}
                      checked={optOutOfEmails}
                      value={optOutOfEmails}
                      label={data!.optOutOfEmails![0].label}
                      id="optOutOfEmails"
                      onClick={() => {
                        setOptOutOfEmails(!optOutOfEmails);
                      }}
                    />
                  )}
                  <TrustBadge />
                </div>
              </Wrapper>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
